import { TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fetchUserByEmail } from "../../utils/dbutils";

// Downloads a text blob to the user's machine.
function downloadText(text, filename) {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Share settings editor for one or more matches. The only sharing mechanism
 * is adding specific users by email address; removing every user clears the
 * share entirely.
 *
 * Props:
 *   shareStatus  number  0 = not shared, 2 = selected users (1/"everyone" is
 *                        retired — sharing is only ever to specific accounts)
 *   shareUsers   string|array  comma-separated emails (or array) for status 2
 *   targetCount  number  how many matches these settings will apply to
 *   sharedBy     string  (optional) if these matches were themselves shared
 *                        with the current user, who shared them — shown as a
 *                        note since a re-share creates the current user's own
 *                        share entry alongside the original one.
 *   onShare      (result|null) => void
 *                result = { shareStatus, shareUsers, includePlaylists } to
 *                save, null to cancel
 */
function Share({ shareStatus, shareUsers, targetCount = 1, sharedBy, onShare }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [includePlaylists, setIncludePlaylists] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [exportName, setExportName] = useState("shared-users");
  const usersListRef = useRef();

  useEffect(() => {
    if (shareUsers !== null && shareUsers !== undefined) {
      const users = Array.isArray(shareUsers)
        ? shareUsers
        : shareUsers
          ? shareUsers.split(",").map((u) => u.trim()).filter(Boolean)
          : [];
      setSelectedUsers(users);
    } else {
      setSelectedUsers([]);
    }
  }, [shareStatus, shareUsers]);

  const doAddUser = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (selectedUsers.includes(email)) {
      toast.error("User already added");
      return;
    }
    setCheckingEmail(true);
    const user = await fetchUserByEmail(email);
    setCheckingEmail(false);
    if (!user?.userUid) {
      toast.error(`No TennisStats Pro account found for ${email}`);
      return;
    }
    setSelectedUsers([...selectedUsers, email]);
    setEmailInput("");
  };

  const doDeleteUser = (user) =>
    setSelectedUsers(selectedUsers.filter((u) => u !== user));

  const handleUsersList = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsText(file, "UTF-8");
    reader.onload = (e) => {
      const incoming = e.target.result
        .split(/[\n,]/)
        .map((u) => u.trim().toLowerCase())
        .filter(Boolean);
      setSelectedUsers((prev) => {
        const next = [...prev];
        for (const u of incoming) if (!next.includes(u)) next.push(u);
        return next;
      });
    };
  };

  const doSaveList = () => {
    if (selectedUsers.length === 0) {
      toast.error("There are no users to save");
      return;
    }
    setExportName("shared-users");
    setShowFilenameModal(true);
  };

  const doConfirmSaveList = () => {
    const filename = exportName.trim();
    if (!filename) {
      toast.error("Please enter a valid filename");
      return;
    }
    downloadText(selectedUsers.join("\n"), filename + ".txt");
    setShowFilenameModal(false);
  };

  const doConfirm = () => {
    onShare({
      shareStatus: selectedUsers.length > 0 ? 2 : 0,
      shareUsers: selectedUsers.join(","),
      includePlaylists,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        {targetCount > 1 && (
          <p className="text-xs text-base-content/60 mb-1">
            These settings will be applied to {targetCount} selected matches.
          </p>
        )}
        {sharedBy && (
          <p className="text-xs text-info mb-1">
            Originally shared with you by {sharedBy}. Re-sharing creates your
            own share entry alongside theirs.
          </p>
        )}

        <div className="flex flex-col mt-2">
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="Enter user email address"
              className="input input-sm input-bordered w-full"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doAddUser()}
            />
            <button
              className="btn btn-sm btn-primary"
              onClick={doAddUser}
              disabled={checkingEmail}
            >
              {checkingEmail ? "Checking…" : "Add"}
            </button>
            <input
              type="file"
              accept=".txt,.csv"
              ref={usersListRef}
              className="hidden"
              onChange={handleUsersList}
              onClick={(e) => {
                e.target.value = null;
              }}
            />
            <button
              className="btn btn-sm btn-outline"
              onClick={() => usersListRef.current?.click()}
            >
              Import
            </button>
          </div>

          <div className="flex flex-col border border-base-300 rounded-box mt-2 p-2">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm text-base-content/70">
                Selected users ({selectedUsers.length})
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-xs btn-outline"
                  onClick={doSaveList}
                  disabled={selectedUsers.length === 0}
                >
                  Save List
                </button>
                <button
                  className="btn btn-xs btn-outline btn-error"
                  onClick={() => setShowClearModal(true)}
                  disabled={selectedUsers.length === 0}
                >
                  Clear List
                </button>
              </div>
            </div>
            <ul className="w-full h-[24vh] border border-base-300 rounded overflow-auto">
              {selectedUsers.length === 0 ? (
                <li className="text-sm text-base-content/40 p-2">
                  No users added yet.
                </li>
              ) : (
                selectedUsers.map((user, idx) => (
                  <li
                    key={user}
                    className={
                      idx % 2 === 0
                        ? "text-sm p-2 bg-base-200/60"
                        : "text-sm p-2"
                    }
                  >
                    <div className="flex justify-between items-center">
                      <span className="truncate">{user}</span>
                      <button
                        className="btn btn-ghost btn-xs text-error"
                        title="Remove"
                        onClick={() => doDeleteUser(user)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>

        <label className="label cursor-pointer justify-start gap-2 mt-2">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={includePlaylists}
            onChange={(e) => setIncludePlaylists(e.target.checked)}
          />
          <span className="label-text">
            Include comments &amp; playlists made from this match
          </span>
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button className="btn btn-sm btn-ghost" onClick={() => onShare(null)}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={doConfirm}>
          Confirm
        </button>
      </div>

      {showClearModal && (
        <div className="modal modal-open z-[9999]">
          <div className="modal-box max-w-sm">
            <h3 className="font-bold text-lg mb-2">Clear Users List</h3>
            <p className="text-sm mb-4">
              Do you really want to remove all users from the list?
            </p>
            <div className="modal-action">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setShowClearModal(false)}
              >
                No
              </button>
              <button
                className="btn btn-sm btn-error"
                onClick={() => {
                  setSelectedUsers([]);
                  setShowClearModal(false);
                }}
              >
                Yes, clear
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setShowClearModal(false)}
          />
        </div>
      )}

      {showFilenameModal && (
        <div className="modal modal-open z-[9999]">
          <div className="modal-box max-w-sm">
            <h3 className="font-bold text-lg mb-2">Save Users List</h3>
            <label className="text-sm text-base-content/70">
              Enter a name for the file:
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full mt-1"
              value={exportName}
              onChange={(e) => setExportName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doConfirmSaveList()}
            />
            <div className="modal-action">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setShowFilenameModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-sm btn-primary" onClick={doConfirmSaveList}>
                Save
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setShowFilenameModal(false)}
          />
        </div>
      )}
    </>
  );
}

export default Share;
